import React, { useState, useEffect } from "react";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { useDroppable } from "@dnd-kit/core";
import { DeleteDialogItem } from "./DeleteDialogItem";
import { TableItem } from "./TableItem";
import { TooltipButton } from "components/common/TooltipButton";
import { TransferTable, fadeOutClass } from "./TransferTypes";

interface DropTargetProps {
  targetId: string;
  droppablePrefix: string;
  renderHeader: () => React.ReactNode;
  isDragOver: boolean;
  assignedTables: TransferTable[];
  onConfirmTransfer: (tableId: string, targetId: string) => Promise<void>;
  onTransferAll?: (targetId: string, tables: TransferTable[]) => Promise<void>;
  transferringTableId: string | null;
  transferringBatchIds?: Set<string>;
  transferSuccessTableIds?: Set<string>;
  fadingOutTableIds?: Set<string>;
  transferErrorTableIds?: Set<string>;
  selectedTables: Set<string>;
  handleTableClick: (
    tableId: string,
    event: React.MouseEvent,
    orderedTableIds: string[],
  ) => void;
  draggedTableIds: Set<string>;
  transferAllTooltip?: (count: number) => string;
  transferTooltip?: string;
}

export const DropTarget: React.FC<DropTargetProps> = ({
  targetId,
  droppablePrefix,
  renderHeader,
  isDragOver,
  assignedTables,
  onConfirmTransfer,
  onTransferAll,
  transferringTableId,
  transferringBatchIds = new Set(),
  transferSuccessTableIds = new Set(),
  fadingOutTableIds = new Set(),
  transferErrorTableIds = new Set(),
  selectedTables,
  handleTableClick,
  draggedTableIds,
  transferAllTooltip,
  transferTooltip,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `${droppablePrefix}-${targetId}`,
  });

  const [expanded, setExpanded] = useState(true);
  const [isTransferringAll, setIsTransferringAll] = useState(false);

  useEffect(() => {
    if (isOver) {
      setExpanded(true);
    }
  }, [isOver]);

  const isActive = isOver || isDragOver;
  const hasAssignedTables = assignedTables.length > 0;

  const handleTransferAll = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onTransferAll || isTransferringAll || assignedTables.length === 0)
      return;
    setIsTransferringAll(true);
    try {
      await onTransferAll(targetId, assignedTables);
    } finally {
      setIsTransferringAll(false);
    }
  };

  const defaultTransferAllTooltip = (count: number) =>
    `Transfer ${count} table${count !== 1 ? "s" : ""}`;

  return (
    <div
      ref={setNodeRef}
      className="rounded-app transition-colors duration-200"
    >
      <div>
        <DeleteDialogItem
          className={`group ${isActive ? "bg-list-selected-light" : ""} transition-colors`}
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center gap-2 w-full">
            <div
              className={`transition-opacity duration-200 ${hasAssignedTables ? "opacity-0 group-hover:opacity-100" : "opacity-0"}`}
            >
              <KeyboardArrowDownRoundedIcon
                className={`icon-sm transition-transform ${expanded ? "" : "-rotate-90"}`}
              />
            </div>

            {renderHeader()}

            {hasAssignedTables && onTransferAll && (
              <div
                className="opacity-0 group-hover:opacity-100 transition-opacity"
                onPointerDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <TooltipButton
                  title={(transferAllTooltip ?? defaultTransferAllTooltip)(
                    assignedTables.length,
                  )}
                  variant="contextual"
                  size="xs"
                  onClick={handleTransferAll}
                  disabled={isTransferringAll || transferringTableId !== null}
                >
                  <SendOutlinedIcon />
                </TooltipButton>
              </div>
            )}
          </div>
        </DeleteDialogItem>
      </div>
      {expanded && assignedTables.length > 0 && (
        <div>
          {assignedTables.map((table) => (
            <div
              key={table.id}
              className={fadeOutClass(fadingOutTableIds.has(table.id))}
            >
              <TableItem
                table={table}
                isSelected={selectedTables.has(table.id)}
                onToggleSelection={(e) =>
                  handleTableClick(
                    table.id,
                    e,
                    assignedTables.map((t) => t.id),
                  )
                }
                isHighlighted={isActive}
                showTransferButton={true}
                onTransfer={() => onConfirmTransfer(table.id, targetId)}
                isTransferring={
                  transferringTableId === table.id ||
                  transferringBatchIds.has(table.id)
                }
                isTransferSuccess={transferSuccessTableIds.has(table.id)}
                isTransferError={transferErrorTableIds.has(table.id)}
                isDragging={draggedTableIds.has(table.id)}
                transferTooltip={transferTooltip}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
